/**
 * screenshare-session — the one orchestrator behind wireless laptop screensharing,
 * shared by the dedicated `/share` page (ShareApp) and the desktop Remote's Share
 * button. It owns a single laptop->TV share end to end: load lib-jitsi-meet, mint
 * Cloudflare Realtime TURN, open a {@link ScreenshareConnection} (getDisplayMedia +
 * ProxyConnectionService), and pump the proxy signalling over the room DO control
 * channel (`CLIENT_PROXY_MESSAGE` out, `JITSI_MEET_UPDATE` in).
 *
 * The TV side is already wired (JitsiMeetingFrame's proxy bridge + the room DO's
 * point-to-point `message` relay), so a captured screen lands on the room's Jitsi
 * stage exactly as legacy Spot's share.8x8.vc page did it — just re-transported off
 * XMPP onto the per-room Durable Object.
 */

import type { IceServer, MessageEnvelope } from '@spaces/contracts';
import { MESSAGES } from '@spaces/contracts';
import { log } from '../log.js';
import type { RemoteControlService } from '../transport/RemoteControlService.js';
import { loadLibJitsiMeet } from './lib-jitsi-meet.js';
import { ScreenshareConnection } from './ScreenshareConnection.js';

export type ShareState = 'idle' | 'starting' | 'sharing';

/** Last-resort ICE config if the credential endpoint is unreachable (LAN only). */
export const STUN_FALLBACK: IceServer[] = [{ urls: 'stun:stun.cloudflare.com:3478' }];

/**
 * Can THIS browser capture a screen to share into the room? `getDisplayMedia` is the
 * real capability gate — it is present on laptop/desktop browsers and ABSENT on the
 * in-room iPad and phones (iOS/iPadOS Safari never expose it), which therefore keep
 * the "open share.8x8.vc on your laptop" hint. A coarse-pointer guard is a belt-and-
 * suspenders second check that keeps the laptop path off a touch-first device on the
 * rare build that does expose the API (a touch laptop still reports a fine pointer,
 * so it keeps the share path).
 */
export function canScreenShareHere(): boolean {
    if (typeof navigator === 'undefined' || typeof window === 'undefined') {
        return false;
    }
    if (typeof navigator.mediaDevices?.getDisplayMedia !== 'function') {
        return false;
    }

    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches ?? false;
    const fine = window.matchMedia?.('(pointer: fine)')?.matches ?? false;

    // Touch-ONLY device (iPad/phone): primary pointer coarse, no fine pointer at all.
    return !(coarse && !fine);
}

/**
 * Fetch short-lived ICE servers (Cloudflare Realtime TURN, minted server-side by
 * pairing) for the screenshare P2P link, via the same-origin `/api` proxy. The TURN
 * API token never touches the browser. Falls back to public STUN if the endpoint is
 * unreachable or returns nothing, so same-network sharing still works.
 */
export async function resolveIceServers(accessToken: string): Promise<IceServer[]> {
    try {
        const res = await fetch('/api/turn-credentials', {
            method: 'POST',
            headers: { authorization: `Bearer ${accessToken}` }
        });

        if (res.ok) {
            const body = await res.json() as { iceServers?: IceServer[] };

            if (Array.isArray(body.iceServers) && body.iceServers.length > 0) {
                log.log('share resolved ICE servers', { count: body.iceServers.length });

                return body.iceServers;
            }
        }

        log.warn('turn-credentials returned no ICE servers; using public STUN', { status: res.status });
    } catch (err) {
        log.warn('turn-credentials fetch failed; using public STUN', { error: String(err) });
    }

    return STUN_FALLBACK;
}

export interface ScreenshareSessionOptions {
    /** external_api.js URL — lib-jitsi-meet is its canonical sibling (see lib loader). */
    externalApiSrc: string;
    /** This client's room endpoint id — the proxy signalling "from". */
    deviceId: string;
    /** The connected room control channel (sends CLIENT_PROXY_MESSAGE, receives JITSI_MEET_UPDATE). */
    rcs: RemoteControlService;
    /** Room access token, for minting TURN credentials. */
    accessToken: string;
    /** State transitions, idle -> starting -> sharing -> idle (drives the UI affordance). */
    onState: (state: ShareState) => void;
    /** Human-readable status line for the UI. */
    onStatus: (status: string) => void;
}

/**
 * One laptop->TV wireless screenshare, driven start to finish. Create it once a room
 * control channel is connected; call {@link start} with the Spot-TV's endpoint id to
 * begin, {@link stop} to end, and route every inbound `message` frame through
 * {@link handleMessage} (it ignores anything that isn't a proxy reply).
 */
export class ScreenshareSession {
    private readonly options: ScreenshareSessionOptions;
    private conn: ScreenshareConnection | null = null;
    private currentState: ShareState = 'idle';

    constructor(options: ScreenshareSessionOptions) {
        this.options = options;
    }

    get state(): ShareState {
        return this.currentState;
    }

    private setState(state: ShareState): void {
        this.currentState = state;
        this.options.onState(state);
    }

    /**
     * Feed an inbound room DO message into the live connection. Ignores anything that
     * isn't a `JITSI_MEET_UPDATE` proxy reply, so the caller can route ALL `message`
     * frames here unconditionally.
     */
    handleMessage(frame: MessageEnvelope): void {
        if (frame.message === MESSAGES.JITSI_MEET_UPDATE && this.conn) {
            this.conn.processMessage({ from: frame.from, data: frame.data });
        }
    }

    /**
     * Capture the laptop screen (the browser shows its screen/window picker) and proxy
     * it into the TV's meeting. `spotId` is the Spot-TV's room endpoint id. No-op if a
     * share is already starting or live; cancelling the picker resolves back to idle.
     */
    async start(spotId: string): Promise<void> {
        if (this.currentState !== 'idle') {
            return;
        }
        if (!spotId) {
            this.options.onStatus('The room TV is not connected yet — try again in a moment.');

            return;
        }

        this.setState('starting');
        this.options.onStatus('Loading the screen-share engine…');

        try {
            await loadLibJitsiMeet(this.options.externalApiSrc);
        } catch (err) {
            this.options.onStatus(`Could not load the screen-share engine: ${(err as Error).message}`);
            this.setState('idle');

            return;
        }

        // Short-lived ICE servers (Cloudflare Realtime TURN, minted by pairing) so the
        // P2P link traverses NAT when the laptop and room are on different networks;
        // falls back to public STUN if the endpoint returns nothing (same-network only).
        this.options.onStatus('Setting up the connection…');
        const iceServers = await resolveIceServers(this.options.accessToken);

        // Stopped while we were loading/minting — bail before opening the picker. Read
        // through the getter so it isn't narrowed away by the `!== 'idle'` guard above
        // (the field is mutated via setState, which TS control-flow can't see).
        if (this.state !== 'starting') {
            return;
        }

        try {
            const conn = new ScreenshareConnection({
                pcConfig: { iceServers },
                onSendMessage: (peerJid, data) => {
                    this.options.rcs.sendMessage({
                        kind: 'message',
                        message: MESSAGES.CLIENT_PROXY_MESSAGE,
                        from: this.options.deviceId,
                        to: peerJid || spotId,
                        data
                    });
                },
                onConnectionClosed: () => {
                    this.options.onStatus('Screen sharing ended.');
                    this.stop('connection-closed');
                }
            });

            this.conn = conn;
            this.options.onStatus('Choose a window or screen to share…');
            await conn.startScreenshare(spotId);
            this.setState('sharing');
            this.options.onStatus('Your screen is sharing into the room.');
        } catch (err) {
            log.error('screenshare start failed', { error: String(err) });
            this.options.onStatus(`Could not start sharing: ${(err as Error).message}`);
            this.stop('start-failed');
        }
    }

    /** Tear down the live screenshare and return to idle (idempotent). */
    stop(reason: string): void {
        if (this.conn) {
            log.log('screenshare session teardown', { reason });
            this.conn.stop();
            this.conn = null;
        }
        this.setState('idle');
    }
}
